// Response shapes come from the shared server/client contracts (SCRUM-983).
import type { AxiosResponse } from "axios";
import type {
  ProductPropertiesResponse,
  ProductPropertyByIdResponse,
  ProductsWithoutPropertiesResponse,
  MessageResponse,
} from "../../../../shared/apiTypes";
import api from "../api";
import { apiWriteThunk } from "../apiWriteThunk";
import createAppAsyncThunk from "../createAppAsyncThunk";

import {
  GET_ALL_PP,
  CLEAR_MESSAGE,
  GET_PP,
  GET_PRODUCTS,
  ADD_PRODUCT_PROPERTIES,
  UPDATE_PRODUCTPROPERTIES,
  CLEAR_PP
} from "./ProductPropertiesTypes";
import { GET_ERRORS } from "../errorServices/errorTypes";
import { asCaught } from "../caughtError";

export const clearMessage = () => (dispatch) => {
  dispatch({
    type: CLEAR_MESSAGE,
  });
};
// Get all pp — SCRUM-1019: createAppAsyncThunk + guarded reverse. The old chain
// had no .catch (unhandled rejection on failure) and crashed on a non-array
// response. Legacy GET_ALL_PP kept (ProductPropertiesSlice answers it, SCRUM-1953).
export const getProductProperties = createAppAsyncThunk(
  "productProperties/getAll",
  async (_arg: void, { dispatch }) => {
    const res: AxiosResponse<ProductPropertiesResponse> = await api.get("/api/productproperties/");
    dispatch({
      type: GET_ALL_PP,
      payload: Array.isArray(res.data) ? [...res.data].reverse() : [],
    });
    return res.data;
  }
);
// Get  pp
export const getProductPropertiesById = (id) => async (dispatch) => {
  try {
    const res: AxiosResponse<ProductPropertyByIdResponse> = await api
      .get(`/api/productproperties/${id}`);
    return dispatch({
      type: GET_PP,
      payload: res.data,
    });
  } catch (err) {
    // Not-found (404) or fetch error: degrade to "no product properties" so a
    // caller that awaits this (e.g. the page-save flow) does not abort. Mirrors
    // the prior 200+null behaviour rather than rejecting.
    return dispatch({
      type: GET_PP,
      payload: null,
    });
  }
};
// SCRUM-1019: createAppAsyncThunk + guarded reverse (was no .catch + crash on a
// non-array response). Keeps dispatching the legacy GET_PP action.
export const getProductPropertiesByProductId = createAppAsyncThunk(
  "productProperties/getByProductId",
  async (id: string, { dispatch }) => {
    const res: AxiosResponse<ProductPropertiesResponse> = await api.get(`/api/productproperties/productid/${id}`);
    dispatch({
      type: GET_PP,
      payload: Array.isArray(res.data) ? [...res.data].reverse() : [],
    });
    return res.data;
  }
);
// Clear pp from store 
export const clearProductPropertiesById = () => (dispatch) => {

      dispatch({
        type: CLEAR_PP,
      })

};

// SCRUM-840: createAppAsyncThunk so a failed DELETE rejects (the
// ManageProductPropertiesContainer caller no longer shows "deleted
// successfully" on failure) and surfaces via the central error hub.
export const deleteProductProperties = createAppAsyncThunk(
  "productProperties/delete",
  async (id: string, { dispatch }) => {
    await api.delete<unknown>(`/api/productproperties/delete/${id}`);
    dispatch(getProductProperties());
    return id;
  }
);
//get products
export const getProducts = () => async (dispatch) => {
  const res: AxiosResponse<ProductsWithoutPropertiesResponse> = await api
    .get(
      "/api/productproperties/toadd/products"
    );
  dispatch({
    type: GET_PRODUCTS,
    payload: res.data,
  });
};

// SCRUM-2119. getProducts is not awaited, matching the old chain: its promise
// was not returned, so a follow-up read failure did not fail the add.
export const addingProductProperties = apiWriteThunk(
  "productProperties/add",
  (data) => api.post<MessageResponse>("/api/productproperties/add", data),
  (dispatch, body) => {
    dispatch({ type: ADD_PRODUCT_PROPERTIES, payload: body });
    dispatch(getProducts());
  }
);
//update product
export const updateProductproperties = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<MessageResponse> = await api.put(
      "/api/productproperties/update",
      data
    );
    
    dispatch({
      type: UPDATE_PRODUCTPROPERTIES,
      payload: res.data,
    });

    await dispatch(getProducts());
    await dispatch(getProductPropertiesById(data._id));

  } catch (err) {
    // The error hub is read by UpdateProductPropertiesContainer as
    // `errors.errors[0].msg` (mirroring this route's own validation shape,
    // `{ errors: [{ msg }] }`). A network-level failure has no `err.response`,
    // so the fallback must use that same shape or the toast never renders and
    // the failure is silent.
    dispatch({
      type: GET_ERRORS,
      payload: asCaught(err).response ? asCaught(err).response?.data : { errors: [{ msg: 'An error occurred' }] },
    });
  }
};
// SCRUM-2119. getProducts stays fire-and-forget, as the old chain did not
// return its promise; the by-id read is awaited because that chain did.
export const patchProductproperties = apiWriteThunk(
  "productProperties/patch",
  (data: { _id: string }) => api.patch<MessageResponse>("/api/productproperties/patch", data),
  async (dispatch, body, data) => {
    dispatch({ type: UPDATE_PRODUCTPROPERTIES, payload: body });
    dispatch(getProducts());
    await dispatch(getProductPropertiesById(data._id));
  }
);
